import React from 'react';
import { AlertCircle } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero */}
      <div className="bg-gradient-to-r from-gray-950 to-black py-20 border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4">
            Terms & <span className="text-yellow-400">Conditions</span>
          </h1>
          <p className="text-gray-400 text-lg">Last Updated: November 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="py-16 bg-gradient-to-b from-black to-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {/* Important Notice */}
            <div className="bg-yellow-400/10 border border-yellow-400/20 rounded-xl p-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="text-yellow-400 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="text-white font-bold text-lg mb-2">Important Notice</h3>
                  <p className="text-gray-300 text-sm">
                    Please read these terms and conditions carefully before participating in any bungee jumping activity. By booking with us, you agree to comply with all terms outlined below.
                  </p>
                </div>
              </div>
            </div>

            {/* Age & Weight Requirements */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4">1. Age & Weight Requirements</h2>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Minimum age: <strong className="text-white">10 years</strong></span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Maximum age: <strong className="text-white">65 years</strong> (with good health)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Minimum weight: <strong className="text-white">40 kg</strong></span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Maximum weight: <strong className="text-white">110 kg</strong></span>
                </li>
              </ul>
            </section>

            {/* Medical Restrictions */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4">2. Medical Restrictions</h2>
              <p className="text-gray-300 mb-4">Jumpers with the following medical conditions are NOT allowed to jump:</p>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  'Any heart conditions',
                  'High blood pressure',
                  'Pregnancy',
                  'Back ache or spine issues',
                  'Spondylitis',
                  'Recent fractures',
                  'Any dislocations',
                  'Epilepsy',
                  'Osteoporosis'
                ].map((condition, index) => (
                  <div key={index} className="flex items-start text-gray-300">
                    <span className="text-red-400 mr-2">✗</span>
                    <span>{condition}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Safety Standards */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4">3. Safety Standards</h2>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>We follow SANZ (Standards of Australia and New Zealand) protocols</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>All equipment is certified and regularly maintained</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Our jump masters are internationally certified</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span>Equipment is checked before every single jump</span>
                </li>
              </ul>
            </section>

            {/* General Terms */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4">4. General Terms</h2>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span><strong className="text-white">ID Requirement:</strong> Valid ID must be presented on arrival (PAN card not accepted)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span><strong className="text-white">Liability Waiver:</strong> A signed waiver is required before participation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span><strong className="text-white">Alcohol/Drugs:</strong> If under the influence, jump will be cancelled without refund</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span><strong className="text-white">Safety Instructions:</strong> Failure to comply may result in cancellation without refund</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">•</span>
                  <span><strong className="text-white">Prohibited Items:</strong> Sharp objects, lighters, luggage, alcohol, knives are not allowed</span>
                </li>
              </ul>
            </section>

            {/* Media Usage */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-4">5. Photos & Videos</h2>
              <p className="text-gray-300">
                Photos and videos taken during your jump may be used for promotional purposes unless you specifically request otherwise. Professional photos and videos are available for purchase.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;